// Arma el `DatosEncabezadoMsp` completo (paciente vía hook + especialidad del
// practicante vía hook + los campos editables de `EncabezadoMspCampos`) para
// cada uno de los 10 formularios del Anexo 1 MSP — mismo criterio que ya
// resuelve `RecetaWizard` para su propio PDF (paciente/sexo/edad vía hooks,
// en el momento de armar los datos). Evita repetir esta misma lógica en los
// 10 formularios (010A/010B/012A/012B/013A/013B/118/122/054/055).
import type { Patient, Reference } from '@medplum/fhirtypes';
import { useResource } from '@medplum/react-hooks';
import { useTranslation } from 'react-i18next';
import { especialidadPorCodigo } from '@/lib/catalogoEspecialidades';
import { useEspecialidadDelPracticante } from '@/lib/useEspecialidadDelPracticante';
import { SISTEMA_HCU } from '../paciente/DatosDemograficosTab';
import type { CamposEncabezadoMsp } from './EncabezadoMspCampos';
import { citaLegalMsp, edadMsp, type DatosEncabezadoMsp } from '@logiacare/hce-pdf';

export interface OpcionesDatosEncabezadoMsp {
  /** 117 — pie con "Cédula" en vez de "Registro MSP/ACESS" (ver `PanelFirmaImpresion`/`generarPdfFormularioMsp`). */
  readonly etiquetaIdentificacion?: string;
  /** 116-cert — el Excel oficial solo trae columnas M/A para la edad, no H/D (ver `edadMsp`). */
  readonly soloMesesAnios?: boolean;
  /**
   * 116-cert — el código MSP real es "116" (`SNS-MSP/HCU-form.116/2021`),
   * distinto del código interno "116-cert" que usa el registro/rutas (nombre
   * heredado de cuando se creía, incorrectamente, que UCI Adulto también
   * usaba "116" — corregido 2026-09-16 a su código real, 113, ver PLAN.md).
   * Sin este override, `citaLegalMsp(codigo)` filtraría el sufijo interno
   * "-cert" al texto legal del PDF.
   */
  readonly citaLegalCodigo?: string;
  /**
   * 028 (Test de Denver II) — la cita legal real impresa en el formulario
   * oficial (`MSP/DNEAIS-HCU-form.028/mayo/2015`) no sigue el patrón fijo
   * `SNS-MSP/HCU-form.<código>/2021` de `citaLegalMsp` (otro prefijo, "mayo"
   * en vez de solo el año) — con esto se manda tal cual, sin pasar por la
   * plantilla genérica.
   */
  readonly citaLegalCompleta?: string;
  /** Certificados (117, 116-cert): el PDF debe entrar en una sola hoja (encabezado y pares compactos). */
  readonly unaHoja?: boolean;
  /** 024 — imprime la cédula del paciente junto al nombre (el PDF anterior del 024 la traía). */
  readonly conIdentificacionPaciente?: boolean;
  /** 116-cert — el bloque A del papel no trae servicio ni especialidad: el encabezado no los imprime. */
  readonly sinServicioNiEspecialidad?: boolean;
  /** El papel no trae servicio y 'Consulta externa' sería engañoso: el encabezado no imprime servicio (sí la especialidad). */
  readonly sinServicio?: boolean;
  /** Servicio capturado por el formulario (012A/012B) — reemplaza al "Consulta externa" fijo del encabezado. */
  readonly servicio?: string;
}

export function useDatosEncabezadoMsp(
  subject: Reference<Patient>,
  campos: CamposEncabezadoMsp,
  codigo: string,
  tituloFormulario: string,
  opts: OpcionesDatosEncabezadoMsp = {},
): DatosEncabezadoMsp {
  const { t } = useTranslation();
  const patient = useResource<Patient>(subject);
  const especialidadDelPracticante = useEspecialidadDelPracticante();
  const nombreEspecialidad = especialidadDelPracticante.cargando
    ? ''
    : (especialidadDelPracticante.codigo && especialidadPorCodigo(especialidadDelPracticante.codigo)?.nombre) || '';

  return {
    institucion: campos.institucion,
    unicodigo: campos.unicodigo || undefined,
    establecimiento: campos.establecimiento || undefined,
    numeroHcu: patient?.identifier?.find((i) => i.system === SISTEMA_HCU)?.value,
    numeroArchivo: campos.numeroArchivo || undefined,
    hoja: campos.hoja,
    pacienteNombre: patient?.name?.[0] ? `${patient.name[0].given?.join(' ') ?? ''} ${patient.name[0].family ?? ''}`.trim() : '',
    pacienteIdentificacion: opts.conIdentificacionPaciente ? patient?.identifier?.[0]?.value : undefined,
    pacienteSexo: patient?.gender ? t(`fichaPaciente.genero.${patient.gender}`) : undefined,
    pacienteEdad: edadMsp(patient?.birthDate, { soloMesesAnios: opts.soloMesesAnios }),
    pacienteFechaNacimiento: patient?.birthDate,
    especialidad: nombreEspecialidad,
    codigo,
    tituloFormulario,
    citaLegal: (opts.citaLegalCompleta ?? citaLegalMsp(opts.citaLegalCodigo ?? codigo)),
    etiquetaIdentificacion: opts.etiquetaIdentificacion,
    unaHoja: opts.unaHoja,
    servicio: opts.servicio || undefined,
    sinServicioNiEspecialidad: opts.sinServicioNiEspecialidad,
    sinServicio: opts.sinServicio,
  };
}
